<script setup>
  import { computed, onMounted, onBeforeUnmount, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { X } from "lucide-vue-next";
  import { useToastsStore } from "@/stores/toastsStore";

  const VISIBLE_LIMIT = 3;
  // Sonner uses a 400ms transform transition when toasts shift; poll a bit longer.
  const POLL_DURATION_MS = 600;

  const toastsStore = useToastsStore();
  const { count } = storeToRefs(toastsStore);

  const visible = computed(() => count.value >= 2);
  const hiddenCount = computed(() => Math.max(0, count.value - VISIBLE_LIMIT));

  const bottomOffset = ref(20);

  let toaster = null;
  let mutationObserver = null;
  let resizeObservers = [];
  let findRafId = null;
  let pollRafId = null;
  let pollUntil = 0;

  function recompute() {
    if (!toaster) return;
    const toasts = toaster.querySelectorAll(
      '[data-sonner-toast][data-visible="true"]:not([data-removed="true"])',
    );
    if (!toasts.length) {
      bottomOffset.value = 20;
      return;
    }
    let topY = window.innerHeight;
    toasts.forEach((el) => {
      const top = el.getBoundingClientRect().top;
      if (top < topY) topY = top;
    });
    bottomOffset.value = window.innerHeight - topY + 12;
  }

  // Sonner shifts toasts via a 400ms CSS transform transition. A single
  // recompute on the next frame catches the start of the transition, not the
  // end — so the pill snaps to where the topmost toast was passing through
  // and gets stranded behind it. Poll every frame for the transition's
  // duration so the pill tracks the toast all the way to its final position.
  function startPolling() {
    pollUntil = performance.now() + POLL_DURATION_MS;
    if (pollRafId !== null) return;
    const tick = () => {
      recompute();
      if (performance.now() < pollUntil) {
        pollRafId = requestAnimationFrame(tick);
      } else {
        pollRafId = null;
      }
    };
    pollRafId = requestAnimationFrame(tick);
  }

  function refreshResizeObservers() {
    resizeObservers.forEach((o) => o.disconnect());
    resizeObservers = [];
    toaster.querySelectorAll('[data-sonner-toast]').forEach((el) => {
      const ro = new ResizeObserver(startPolling);
      ro.observe(el);
      resizeObservers.push(ro);
    });
    startPolling();
  }

  function findAndAttach() {
    toaster = document.querySelector('[data-sonner-toaster]');
    if (!toaster) {
      findRafId = requestAnimationFrame(findAndAttach);
      return;
    }
    mutationObserver = new MutationObserver((mutations) => {
      // childList → toast added/removed → re-attach resize observers.
      // attribute (style/data-removed/data-visible) → reposition only.
      // The `style` attr captures sonner's --offset updates when the stack shifts.
      const hasChildList = mutations.some((m) => m.type === "childList");
      if (hasChildList) refreshResizeObservers();
      else startPolling();
    });
    mutationObserver.observe(toaster, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-removed", "data-visible", "style"],
    });
    refreshResizeObservers();
  }

  onMounted(findAndAttach);

  onBeforeUnmount(() => {
    if (findRafId) cancelAnimationFrame(findRafId);
    if (pollRafId) cancelAnimationFrame(pollRafId);
    mutationObserver?.disconnect();
    resizeObservers.forEach((o) => o.disconnect());
  });
</script>

<template>
  <div
    v-if="visible"
    class="fixed right-5 z-toast flex items-center gap-2 font-sans pointer-events-none [&>*]:pointer-events-auto print:hidden"
    :style="{ bottom: `${bottomOffset}px` }">
    <span
      v-if="hiddenCount > 0"
      class="bg-white border border-border rounded-full px-2.5 py-1 text-2xs text-muted-foreground shadow-md">
      +{{ hiddenCount }} hidden
    </span>
    <button
      type="button"
      class="inline-flex items-center justify-center gap-1.5 bg-episto-navy text-white border-0 rounded-full px-3 py-1.5 text-xs font-semibold leading-none cursor-pointer shadow-md transition-[background,transform] duration-200 hover:bg-episto-navy-800 active:translate-y-px focus-visible:outline-2 focus-visible:outline focus-visible:outline-episto-teal focus-visible:outline-offset-2"
      :aria-label="`Close all (${count})`"
      @click="toastsStore.dismissAll()">
      <X
        :size="12"
        :stroke-width="2.6"
        class="shrink-0" />
      <span>Close all ({{ count }})</span>
    </button>
  </div>
</template>
